import DialogTitle from '@mui/material/DialogTitle'
import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import { styled } from '@mui/material'
import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PAGE_MAX_WIDTH } from '../../../Styles'
import ProcessSteps from './ProcessSteps'
import { HBButton } from '../../../Components/Material/Card/HBButton'
import { CloseModalIcon } from '../../../Components/Material/Modal/Icons'
import { ProcessStepsProps } from './ProcessSteps/ProcessSteps'

const SHOW_INTRO_PARAM_NAME = 'show-intro-modal'

const BriefIntroTitle = styled(DialogTitle)(({ theme }) => ({
  textAlign: 'center',
  fontSize: '1.5rem',
  fontWeight: 600,
  padding: 0,
  marginBottom: 16,
  marginTop: 40,
  [theme.breakpoints.up('md')]: {
    marginTop: 56,
    fontSize: '2.5rem',
    fontWeight: 700,
    marginBottom: 24,
  },
}))

const StyledDialog = styled(Dialog)(({ theme }) => ({
  ' .MuiDialog-container': {
    alignItems: 'flex-start',
    margin: 16,
    [theme.breakpoints.up('md')]: {
      marginBottom: 32,
      marginTop: 64,
    },
  },
  ' .MuiPaper-root': {
    padding: 16,
    paddingBottom: 32,
    margin: 0,
    maxWidth: 416,
    maxHeight: 'calc(100% - 32px)',
    display: 'block',
    [theme.breakpoints.up('md')]: {
      padding: 24,
      paddingBottom: 32,
      margin: '0 32px',
      maxWidth: PAGE_MAX_WIDTH - 64, // subtract gutters defined below
      maxHeight: 'calc(100% - 96px)',
    },
  },
}))

const Button = styled(HBButton)(({ theme }) => ({
  padding: '7px 43.5px',
  width: 'fit-content',
  margin: '32px auto 0 auto',
  display: 'block',
  '&:focus, &:focus-visible': {
    outline: '3px solid #1270B8',
  },
  '&:active': {
    outline: 'none',
    transform: 'scale(0.96)',
  },
}))

interface BriefIntroModalProps extends ProcessStepsProps {
  showIntroModal: boolean
}

const BriefIntroModal = ({
  isDirectEnquiry,
  venueName,
  showIntroModal,
}: BriefIntroModalProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false)
  const [params, setSearchParams] = useSearchParams()
  const openParam = params.get(SHOW_INTRO_PARAM_NAME) ?? ''
  const [shouldOpen] = useState(
    showIntroModal || openParam.toLowerCase() === 'true'
  )

  const onClose = (): void => setIsOpen(false)

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsOpen(shouldOpen)
    }, 700)
    if (shouldOpen) {
      // remove the modal-triggering query string from the URL once used
      params.delete(SHOW_INTRO_PARAM_NAME)
      setSearchParams(params, { replace: true })
    }
    return () => clearTimeout(timeout)
  }, [shouldOpen])

  return (
    <StyledDialog open={isOpen} transitionDuration={500} onClose={onClose}>
      <IconButton
        aria-label='close'
        onClick={onClose}
        sx={{
          float: 'right',
          '&:active': { transform: 'scale(0.95)' },
          '&:focus': { outline: '3px solid #1270B8' },
          ' .MuiSvgIcon-root': {
            width: 16,
            height: 'auto',
          },
        }}
      >
        <CloseModalIcon />
      </IconButton>
      <BriefIntroTitle>The perfect venue is on their way!</BriefIntroTitle>
      <ProcessSteps isDirectEnquiry={isDirectEnquiry} venueName={venueName} />
      <Button onClick={onClose}>Close</Button>
    </StyledDialog>
  )
}

export default BriefIntroModal
